// client/src/components/admin/CableRouteTester.jsx
// Admin-only cable-route test surface. The admin clicks the FAT then the
// building on the map; a pin drops at the EXACT clicked point immediately, then
// the app calls fetchCableRoute and draws the result as fibre-optic light
// propagating down the cable (glowing comet head + glass-clad halo + dashed
// teal core), settling into a slow data-flow shimmer. Isolated from the live
// operator annotation flow.
import {
  useEffect, useRef, useState, useCallback,
} from 'react';
import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import { fetchCableRoute } from '../../lib/cableRouteApi';
import WorkspaceSwitcher from '../WorkspaceSwitcher';

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// One-time scoped stylesheet: the pin-drop keyframe (so the exact click point
// is unmistakable) plus a reduced-motion guard.
const ANIM_STYLE_ID = 'cr-anim-style';
const ANIM_CSS = `
@keyframes crPinDrop {
  0%   { transform: translateY(-15px) scale(0.55); opacity: 0; }
  55%  { transform: translateY(3px)  scale(1.10); opacity: 1; }
  78%  { transform: translateY(-1px) scale(0.97); }
  100% { transform: translateY(0)    scale(1.00); opacity: 1; }
}
.cr-pin { animation: crPinDrop 380ms cubic-bezier(.22,1,.36,1) both;
  transform-origin: 50% 100%; will-change: transform, opacity; }
@media (prefers-reduced-motion: reduce) { .cr-pin { animation: none; } }
`;
function ensureAnimStyle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(ANIM_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = ANIM_STYLE_ID;
  el.textContent = ANIM_CSS;
  document.head.appendChild(el);
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Dark-glass panel - mirrors AnnotationMap's MapTypeToggle/DrawHUD tokens exactly.
const panelStyle = {
  position: 'absolute',
  top: 14,
  insetInlineEnd: 14,
  zIndex: 6,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  minWidth: 268,
  maxWidth: 320,
  padding: '13px 15px',
  background: 'linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.93) 100%)',
  borderRadius: 13,
  border: '1px solid rgba(148,163,184,0.24)',
  borderTop: '3px solid #2dd4bf',
  boxShadow:
    '0 1px 0 rgba(255,255,255,0.07) inset, 0 10px 30px rgba(2,6,23,0.6), 0 1px 3px rgba(0,0,0,0.45)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  direction: 'rtl',
  fontFamily: "'Vazirmatn', sans-serif",
};

// One-time scoped pulse keyframe for the active step. Lives in panel CSS so
// only the running step animates while the others stay perfectly still.
const STEP_KF_ID = 'cr-step-style';
const STEP_KF = '@keyframes crStepPulse{0%{box-shadow:0 0 0 4px rgba(45,212,191,0.30)}70%{box-shadow:0 0 0 9px rgba(45,212,191,0)}100%{box-shadow:0 0 0 4px rgba(45,212,191,0)}}';
function ensureStepStyle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STEP_KF_ID)) return;
  const el = document.createElement('style');
  el.id = STEP_KF_ID;
  el.textContent = STEP_KF;
  document.head.appendChild(el);
}

// Pin-coloured tones so each step row visibly belongs to the pin it picks:
// amber for FAT, emerald for the building.
const STEP_TONE = {
  amber: { fill: '#f59e0b', border: 'rgba(245,158,11,0.65)', text: '#fde68a' },
  emerald: { fill: '#10b981', border: 'rgba(16,185,129,0.65)', text: '#a7f3d0' },
};

function CheckmarkIcon({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="m3 7.2 3 3 5-6" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// A single row of the two-step picker. The status box on the right (RTL):
//   - done    : filled square in the pin's colour with a checkmark inside
//   - active  : empty teal-bordered square with a pulsing teal dot
//   - idle    : faint outline only
// Coordinates appear under the label once the step is done. The whole row
// dims when disabled (i.e. before the previous step is complete).
function StepRow({
  label, coord, active, done, tone, hint, disabled,
}) {
  const t = tone;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        padding: '9px 11px',
        background: done
          ? 'rgba(2,6,23,0.45)'
          : active ? 'rgba(45,212,191,0.07)' : 'transparent',
        border: `1px solid ${done
          ? 'rgba(148,163,184,0.22)'
          : active ? 'rgba(45,212,191,0.32)' : 'rgba(148,163,184,0.14)'}`,
        borderRadius: 9,
        opacity: disabled ? 0.5 : 1,
        transition: 'background 220ms ease, border-color 220ms ease, opacity 220ms ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            flexShrink: 0,
            borderRadius: 5,
            border: `1.5px solid ${done
              ? t.border
              : active ? 'rgba(45,212,191,0.6)' : 'rgba(148,163,184,0.4)'}`,
            background: done ? t.fill : 'transparent',
            transition: 'background 220ms ease, border-color 220ms ease',
          }}
        >
          {done && <CheckmarkIcon />}
          {active && !done && (
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: '#2dd4bf',
                animation: 'crStepPulse 1.4s ease-out infinite',
              }}
            />
          )}
        </span>
        <span
          style={{
            fontSize: 12.5,
            fontWeight: 700,
            lineHeight: 1.3,
            color: done ? t.text : (active ? '#e2e8f0' : '#94a3b8'),
          }}
        >
          {label}
        </span>
      </div>
      {done && coord && (
        <div
          style={{
            marginInlineStart: 30,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            fontSize: 10.5,
            color: '#94a3b8',
            direction: 'ltr',
            lineHeight: 1.45,
            letterSpacing: '0.01em',
          }}
        >
          {`${coord.lat.toFixed(5)}, ${coord.lng.toFixed(5)}`}
        </div>
      )}
      {!done && hint && (
        <div
          style={{
            marginInlineStart: 30,
            fontSize: 11.5,
            color: active ? '#5eead4' : '#64748b',
            lineHeight: 1.5,
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

// Compact 2-step progress: pick FAT, then pick the building. The rows auto
// tick themselves as the admin clicks the map - no separate sim buttons.
// Status is derived from `pick` (the state machine) rather than the raw coord
// state so an error path that re-enters pick='olt' visually resets both rows
// even if previous coords are still cached.
function StepIndicator({ olt, bld, pick }) {
  useEffect(() => { ensureStepStyle(); }, []);
  const fatDone = pick !== 'olt';
  const bldDone = pick === 'running' || pick === 'done';
  const fatActive = pick === 'olt';
  const bldActive = pick === 'building';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <StepRow
        label="گام ۱: نقطه‌ی FAT"
        coord={fatDone ? olt : null}
        active={fatActive}
        done={fatDone}
        tone={STEP_TONE.amber}
        hint="روی نقشه کلیک کنید تا FAT همان‌جا قرار گیرد"
      />
      <StepRow
        label="گام ۲: نقطه‌ی ساختمان"
        coord={bldDone ? bld : null}
        active={bldActive}
        done={bldDone}
        tone={STEP_TONE.emerald}
        hint={fatDone
          ? 'حالا روی ساختمان کلیک کنید تا مسیر محاسبه شود'
          : 'پس از انتخاب FAT فعال می‌شود'}
        disabled={!fatDone && !bldDone}
      />
    </div>
  );
}

// A single stat row in the result panel.
function StatRow({ label, value }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 8,
        fontSize: 12.5,
        lineHeight: 1.6,
      }}
    >
      <span style={{ color: '#94a3b8', fontWeight: 500 }}>{label}</span>
      <span style={{ color: '#e2e8f0', fontWeight: 700, direction: 'ltr' }}>{value}</span>
    </div>
  );
}

// Build the custom pin DOM (filled dot + light ring + stem + label chip).
function buildPinContent(kind) {
  const amber = kind === 'olt';
  const accent = amber ? '#f59e0b' : '#10b981';
  const ring = amber ? 'rgba(251,191,36,0.35)' : 'rgba(16,185,129,0.32)';
  const wrap = document.createElement('div');
  wrap.className = 'cr-pin';
  wrap.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:3px;pointer-events:none';

  const head = document.createElement('div');
  head.style.cssText = [
    'width:16px', 'height:16px', 'border-radius:50%',
    `background:${accent}`,
    'border:2px solid #f8fafc',
    `box-shadow:0 0 0 5px ${ring}, 0 2px 6px rgba(0,0,0,0.55)`,
    'flex-shrink:0',
  ].join(';');

  const stem = document.createElement('div');
  stem.style.cssText = [
    'width:2px', 'height:7px', `background:${accent}`,
    'opacity:0.85', 'margin-top:-1px', 'border-radius:1px',
  ].join(';');

  const label = document.createElement('div');
  label.textContent = amber ? 'FAT' : 'ساختمان';
  label.style.cssText = [
    'background:rgba(15,23,42,0.9)', 'color:#fff', 'font-size:9px',
    'font-weight:700', "font-family:'Vazirmatn',sans-serif",
    'letter-spacing:0.04em', 'padding:1px 5px', 'border-radius:3px',
    'white-space:nowrap', 'line-height:1.5',
    `border:1px solid ${ring}`,
  ].join(';');

  wrap.appendChild(head);
  wrap.appendChild(stem);
  wrap.appendChild(label);
  return { wrap, title: label.textContent };
}

// The inner component that calls useMap() and owns all overlay + click logic.
function CableRouteLayer() {
  const map = useMap();

  // pick: 'olt' | 'building' | 'running' | 'done'
  const [pick, setPick] = useState('olt');
  const [olt, setOlt] = useState(null);
  const [bld, setBld] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Route overlay refs (cable). Pins are tracked separately so drawing the
  // route never removes the pins the admin placed.
  const dashRef = useRef(null); // dashed teal core
  const glowRef = useRef(null); // soft glass-clad halo underlay
  const cometRef = useRef(null); // propagating light head (AdvancedMarkerElement)
  const rafRef = useRef(null);
  const oltMarkerRef = useRef(null);
  const bldMarkerRef = useRef(null);

  const oltRef = useRef(null);
  const pickRef = useRef('olt');
  // Monotonic run-generation: bumped on reset/unmount/new run so a stale
  // in-flight response or async importLibrary cannot resurrect dead overlays.
  const runIdRef = useRef(0);

  useEffect(() => { ensureAnimStyle(); }, []);

  // Remove only the cable overlays (keep the pins).
  const clearRoute = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (dashRef.current) { dashRef.current.setMap(null); dashRef.current = null; }
    if (glowRef.current) { glowRef.current.setMap(null); glowRef.current = null; }
    if (cometRef.current) { cometRef.current.map = null; cometRef.current = null; }
  }, []);

  const clearPins = useCallback(() => {
    if (oltMarkerRef.current) { oltMarkerRef.current.map = null; oltMarkerRef.current = null; }
    if (bldMarkerRef.current) { bldMarkerRef.current.map = null; bldMarkerRef.current = null; }
  }, []);

  // Full clear (new selection / reset / unmount).
  const clearOverlays = useCallback(() => {
    clearRoute();
    clearPins();
  }, [clearRoute, clearPins]);

  // Drop a labelled pin at the EXACT clicked coordinate immediately, before
  // any routing. kind: 'olt' (amber) | 'building' (emerald). runId-guarded so
  // a reset during importLibrary cannot orphan it.
  const dropPin = useCallback((coord, kind) => {
    if (!map || !window.google || !window.google.maps) return;
    const capturedRunId = runIdRef.current;
    const ref = kind === 'olt' ? oltMarkerRef : bldMarkerRef;
    window.google.maps.importLibrary('marker').then(({ AdvancedMarkerElement }) => {
      if (runIdRef.current !== capturedRunId) return;
      if (ref.current) { ref.current.map = null; ref.current = null; }
      const { wrap, title } = buildPinContent(kind);
      ref.current = new AdvancedMarkerElement({
        map, position: coord, content: wrap, title,
      });
    }).catch(() => { /* pin creation failed silently */ });
  }, [map]);

  // Slow continuous dash flow after the draw completes: persistent "data
  // flowing through the live fibre" - subtle, not noise.
  const startDashFlow = useCallback((line) => {
    if (prefersReducedMotion()) return;
    let off = 0;
    const tick = () => {
      if (dashRef.current !== line) return; // superseded/cleared
      off = (off + 0.35) % 100;
      const icons = line.get('icons');
      if (!icons || !icons[0]) return;
      icons[0].offset = `${off}%`;
      line.set('icons', icons);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  // Draw the route as fibre-optic light propagating from OLT to building.
  const drawRoute = useCallback((routeGeoJson, oltCoord, bldCoord) => {
    if (!map || !window.google || !window.google.maps) return;
    clearRoute();

    const full = routeGeoJson.coordinates.map(([lng, lat]) => ({ lat, lng }));
    if (full.length < 2) return;

    // Glass-clad halo underlay (the cladding glow).
    const glow = new window.google.maps.Polyline({
      map, path: [], strokeColor: '#0d9488', strokeOpacity: 0.26, strokeWeight: 9,
    });
    // Dashed teal core (opacity carried entirely by the dash icon).
    const dash = new window.google.maps.Polyline({
      map,
      path: [],
      strokeOpacity: 0,
      icons: [{
        icon: {
          path: 'M 0,-1 0,1', strokeOpacity: 1, strokeColor: '#2dd4bf', scale: 3,
        },
        offset: '0',
        repeat: '12px',
      }],
    });
    dashRef.current = dash;
    glowRef.current = glow;

    // Frame the route + both endpoints so it is actually visible.
    const bounds = new window.google.maps.LatLngBounds();
    full.forEach((p) => bounds.extend(p));
    if (oltCoord) bounds.extend(oltCoord);
    if (bldCoord) bounds.extend(bldCoord);
    map.fitBounds(bounds, 72);

    // Cumulative planar (cos-lat) length so the light head moves at a
    // constant visual speed regardless of vertex density.
    const segLen = [0];
    for (let i = 1; i < full.length; i += 1) {
      const a = full[i - 1]; const b = full[i];
      const dx = (b.lng - a.lng) * Math.cos((a.lat * Math.PI) / 180);
      segLen.push(segLen[i - 1] + Math.hypot(b.lat - a.lat, dx));
    }
    const totalLen = segLen[segLen.length - 1] || 1;

    const pointAt = (frac) => {
      const target = frac * totalLen;
      let i = 1;
      while (i < segLen.length && segLen[i] < target) i += 1;
      if (i >= full.length) return { path: full.slice(), tip: full[full.length - 1] };
      const a = full[i - 1]; const b = full[i];
      const span = (segLen[i] - segLen[i - 1]) || 1;
      const t = (target - segLen[i - 1]) / span;
      const tip = { lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t };
      return { path: full.slice(0, i).concat([tip]), tip };
    };

    const reduce = prefersReducedMotion();
    if (reduce) {
      dash.setPath(full);
      glow.setPath(full);
      startDashFlow(dash);
      return;
    }

    // Comet head = the propagating photon (radial-gradient orb + soft halo).
    const capturedRunId = runIdRef.current;
    window.google.maps.importLibrary('marker').then(({ AdvancedMarkerElement }) => {
      if (runIdRef.current !== capturedRunId || dashRef.current !== dash) return;
      const orb = document.createElement('div');
      orb.style.cssText = [
        'width:16px', 'height:16px', 'border-radius:50%',
        'background:radial-gradient(circle at 50% 50%, #ccfbf1 0%, #5eead4 42%, rgba(45,212,191,0.18) 74%, rgba(45,212,191,0) 100%)',
        'box-shadow:0 0 10px 3px rgba(45,212,191,0.7), 0 0 22px 9px rgba(45,212,191,0.32)',
        'pointer-events:none',
      ].join(';');
      cometRef.current = new AdvancedMarkerElement({
        map, position: full[0], content: orb, zIndex: 9999,
      });
    }).catch(() => {});

    const DURATION = 1700;
    const t0 = performance.now();
    const ease = (x) => 1 - ((1 - x) ** 3); // ease-out cubic
    const step = (now) => {
      if (dashRef.current !== dash) return; // cleared mid-animation
      const frac = Math.min(1, (now - t0) / DURATION);
      const { path, tip } = pointAt(ease(frac));
      dash.setPath(path);
      glow.setPath(path);
      if (cometRef.current) cometRef.current.position = tip;
      if (frac < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        if (cometRef.current) { cometRef.current.map = null; cometRef.current = null; }
        startDashFlow(dash);
      }
    };
    rafRef.current = requestAnimationFrame(step);
  }, [map, clearRoute, startDashFlow]);

  // Core route-fetch logic, shared between real map clicks and sim buttons.
  const runRoute = useCallback(async (oltCoord, buildingCoord) => {
    const myRun = (runIdRef.current += 1);
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetchCableRoute(oltCoord, buildingCoord);
      if (runIdRef.current !== myRun) return; // superseded
      setLoading(false);
      if (!response) {
        setError('پاسخی از سرور دریافت نشد');
        setPick('olt');
        return;
      }
      const { data, error: apiError } = response;
      if (apiError) {
        setError(apiError);
        setPick('olt');
        return;
      }
      setResult(data);
      setPick('done');
      if (data && data.feasible && data.route && map && window.google && window.google.maps) {
        drawRoute(data.route, oltCoord, buildingCoord);
      }
    } catch (err) {
      if (runIdRef.current !== myRun) return; // superseded
      setLoading(false);
      setError(err && err.message ? err.message : 'خطا در محاسبه مسیر');
      setPick('olt');
    }
  }, [map, drawRoute]);

  useEffect(() => { oltRef.current = olt; }, [olt]);
  useEffect(() => { pickRef.current = pick; }, [pick]);

  // Register the map click listener for OLT and building picking.
  useEffect(() => {
    if (!map) return undefined;

    const onClick = (e) => {
      if (!e.latLng) return;
      const coord = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      const cur = pickRef.current;

      if (cur === 'olt') {
        oltRef.current = coord;
        setOlt(coord);
        setBld(null);
        setResult(null);
        setError(null);
        clearOverlays();
        dropPin(coord, 'olt'); // pin drops at the EXACT click, immediately
        setPick('building');
      } else if (cur === 'building') {
        const currentOlt = oltRef.current;
        dropPin(coord, 'building'); // pin drops at the EXACT click, immediately
        setBld(coord);
        setPick('running');
        pickRef.current = 'running';
        runRoute(currentOlt, coord);
      }
    };

    const listener = map.addListener('click', onClick);
    return () => {
      if (window.google?.maps?.event) {
        window.google.maps.event.removeListener(listener);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- clearOverlays/runRoute/dropPin are stable per map; map drives re-registration
  }, [map]);

  // Cleanup on unmount; bump runId so any in-flight async is ignored.
  useEffect(() => () => { runIdRef.current += 1; clearOverlays(); }, [clearOverlays]);

  // Dev-only shortcuts: fixed coordinates so the existing test and a local
  // developer can drive the flow without manually clicking the map. Rendered
  // only when NODE_ENV !== 'production' so the deployed site never shows
  // them; CRA tree-shakes the buttons out of the production bundle.
  const handleSimOlt = useCallback(() => {
    const coord = { lat: 35.7008, lng: 51.3905 };
    oltRef.current = coord;
    setOlt(coord);
    setBld(null);
    setResult(null);
    setError(null);
    clearOverlays();
    dropPin(coord, 'olt');
    setPick('building');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- oltRef is a ref; clearOverlays/dropPin stable
  }, [clearOverlays, dropPin]);

  const handleSimBuilding = useCallback(() => {
    const currentOlt = oltRef.current;
    if (!currentOlt) return;
    const coord = { lat: 35.6997, lng: 51.3375 };
    setBld(coord);
    dropPin(coord, 'building');
    setPick('running');
    pickRef.current = 'running';
    runRoute(currentOlt, coord);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runRoute/dropPin stable; oltRef is a ref
  }, [runRoute, dropPin]);

  const handleReset = useCallback(() => {
    runIdRef.current += 1;
    oltRef.current = null;
    setOlt(null);
    setBld(null);
    setResult(null);
    setError(null);
    setPick('olt');
    clearOverlays();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- oltRef is a ref; clearOverlays stable
  }, [clearOverlays]);

  const showResult = result !== null && pick === 'done';

  return (
    <div style={panelStyle}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 800,
          color: '#5eead4',
          letterSpacing: '0.01em',
          lineHeight: 1.3,
        }}
      >
        تست مسیر کابل
      </div>

      {!loading && <StepIndicator olt={olt} bld={bld} pick={pick} />}

      {loading && (
        <div style={{ fontSize: 12.5, fontWeight: 600, color: '#94a3b8', lineHeight: 1.5 }}>
          در حال محاسبه...
        </div>
      )}

      {error && !loading && (
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: '#fca5a5',
            lineHeight: 1.5,
            padding: '6px 8px',
            background: 'rgba(127,29,29,0.32)',
            borderRadius: 7,
            border: '1px solid rgba(248,113,113,0.28)',
          }}
        >
          {typeof error === 'string' ? error : 'خطا در محاسبه مسیر'}
        </div>
      )}

      {showResult && !loading && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
            padding: '8px 10px',
            background: 'rgba(2,6,23,0.32)',
            borderRadius: 8,
            border: '1px solid rgba(148,163,184,0.18)',
          }}
        >
          {result.feasible ? (
            <>
              <StatRow label="فاصله" value={`${result.distance_m} متر`} />
              <StatRow
                label="تعداد تقاطع‌ها"
                value={String(result.crossings ? result.crossings.length : 0)}
              />
              {result.widestCrossing && (
                <StatRow
                  label="عریض‌ترین تقاطع"
                  value={`${Math.round(result.widestCrossing.width)} متر`}
                />
              )}
              {Array.isArray(result.crossings) && result.crossings.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 2 }}>
                  {result.crossings.slice(0, 6).map((c, i) => (
                    <div
                      key={`${c.lat}-${c.lng}-${i}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 11.5,
                        color: '#cbd5e1',
                        direction: 'rtl',
                      }}
                    >
                      <span>{c.name || (c.highway ? `معبر ${c.highway}` : 'خیابان')}</span>
                      <span style={{ direction: 'ltr', color: '#94a3b8' }}>
                        {(() => {
                          const wv = c.widthEstimate != null ? c.widthEstimate : c.width;
                          return wv != null ? `${Math.round(wv)} م` : '-';
                        })()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {Array.isArray(result.ruleViolations) && result.ruleViolations.length > 0 && (
                <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.6, color: '#fca5a5' }}>
                  {`${result.ruleViolations.length} نقض قانون کابل‌کشی: عبور از معبر عریض یا ممنوع`}
                </div>
              )}
              {result.gratuitous && (
                <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.6, color: '#fbbf24' }}>
                  مبدا و مقصد یک‌سمت معابر اصلی هستند؛ عبور عریض در این مسیر غیرضروری است
                </div>
              )}
              {result.endpointCrossings > 0 && result.note && (
                <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.6, color: '#fbbf24' }}>
                  {result.note}
                </div>
              )}
            </>
          ) : (
            <div
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: '#fca5a5',
                lineHeight: 1.5,
              }}
            >
              {result.reason || 'مسیر امکان‌پذیر نیست'}
            </div>
          )}
        </div>
      )}

      {(olt || result || error) && (
        <button
          type="button"
          onClick={handleReset}
          style={{
            padding: '7px 14px',
            background: 'transparent',
            color: '#64748b',
            fontFamily: "'Vazirmatn', sans-serif",
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1,
            border: '1px solid rgba(148,163,184,0.22)',
            borderRadius: 7,
            cursor: 'pointer',
            transition: 'background 140ms ease, color 140ms ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(71,85,105,0.45)'; e.currentTarget.style.color = '#e2e8f0'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
        >
          بازنشانی
        </button>
      )}

      {process.env.NODE_ENV !== 'production' && (
        <div
          style={{
            marginTop: 4,
            paddingTop: 8,
            borderTop: '1px dashed rgba(148,163,184,0.18)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
          }}
        >
          <div
            style={{
              flexBasis: '100%',
              fontSize: 10,
              fontWeight: 600,
              color: '#475569',
              letterSpacing: '0.04em',
              lineHeight: 1.4,
            }}
          >
            تست سریع (فقط در حالت توسعه)
          </div>
          <button
            type="button"
            onClick={handleSimOlt}
            style={{
              flex: 1,
              minWidth: 90,
              padding: '5px 8px',
              background: 'rgba(30,41,59,0.55)',
              color: '#94a3b8',
              fontFamily: "'Vazirmatn', sans-serif",
              fontSize: 10.5,
              fontWeight: 600,
              lineHeight: 1,
              border: '1px dashed rgba(148,163,184,0.24)',
              borderRadius: 6,
              cursor: 'pointer',
              transition: 'background 140ms ease, color 140ms ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(51,65,85,0.6)'; e.currentTarget.style.color = '#cbd5e1'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(30,41,59,0.55)'; e.currentTarget.style.color = '#94a3b8'; }}
          >
            شبیه‌سازی FAT
          </button>
          <button
            type="button"
            onClick={handleSimBuilding}
            disabled={!olt}
            style={{
              flex: 1,
              minWidth: 90,
              padding: '5px 8px',
              background: !olt ? 'rgba(15,23,42,0.45)' : 'rgba(30,41,59,0.55)',
              color: !olt ? '#334155' : '#94a3b8',
              fontFamily: "'Vazirmatn', sans-serif",
              fontSize: 10.5,
              fontWeight: 600,
              lineHeight: 1,
              border: `1px dashed ${!olt ? 'rgba(148,163,184,0.14)' : 'rgba(148,163,184,0.24)'}`,
              borderRadius: 6,
              cursor: !olt ? 'not-allowed' : 'pointer',
              transition: 'background 140ms ease, color 140ms ease',
            }}
            onMouseEnter={(e) => { if (olt) { e.currentTarget.style.background = 'rgba(51,65,85,0.6)'; e.currentTarget.style.color = '#cbd5e1'; } }}
            onMouseLeave={(e) => { if (olt) { e.currentTarget.style.background = 'rgba(30,41,59,0.55)'; e.currentTarget.style.color = '#94a3b8'; } }}
          >
            شبیه‌سازی ساختمان
          </button>
        </div>
      )}
    </div>
  );
}

export default function CableRouteTester() {
  return (
    <div style={{ width: '100%', height: '100vh', position: 'relative' }}>
      <APIProvider apiKey={KEY || ''} language="fa" region="IR">
        {KEY ? (
          // Reuses the operator VECTOR map id on purpose: it is a known-good
          // vector style; a new mapId would need Cloud Console setup.
          <Map
            mapId="fttx-operator"
            defaultCenter={{ lat: 35.7, lng: 51.39 }}
            defaultZoom={13}
            gestureHandling="greedy"
            disableDefaultUI={false}
            renderingType="VECTOR"
            defaultTilt={0}
            style={{ width: '100%', height: '100%' }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#0f172a',
            }}
          >
            <div
              style={{
                padding: '24px 32px',
                background: 'linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.93) 100%)',
                borderRadius: 14,
                border: '1px solid rgba(148,163,184,0.22)',
                color: '#fca5a5',
                fontSize: 14,
                fontWeight: 600,
                fontFamily: "'Vazirmatn', sans-serif",
                direction: 'rtl',
              }}
            >
              کلید Google Maps تنظیم نشده است
            </div>
          </div>
        )}
        <CableRouteLayer />
      </APIProvider>
      <div
        style={{
          position: 'absolute',
          top: 14,
          insetInlineStart: 14,
          zIndex: 6,
          direction: 'rtl',
        }}
      >
        <WorkspaceSwitcher current="cable-test" />
      </div>
    </div>
  );
}
